import type { ReactNode } from "react";

import type { Candidate, FactBox, Read } from "@/components/ui/box.schema";
import type { SessionWorkspace } from "@/lib/api/sessionPortal";
import { destinationStatement } from "@/lib/session-destination-copy";
import { formatInCourseZone } from "@/lib/session-portal";
import { formatClassWhen } from "@/lib/seat-workspace";

import { SESSION_COPY } from "./session-copy";

const copy = SESSION_COPY.teach;

/** The session's phase, said as Teach's answer: Now, Ended, Cancelled or when it starts. */
export function teachProse(workspace: SessionWorkspace, readAt: string): string {
  const { session, course } = workspace;
  if (session.status === "cancelled") return copy.cancelled;
  if (session.clock_phase === "scheduled_now") return copy.now;
  if (session.status === "held" || session.clock_phase === "status_pending") return copy.ended;
  return copy.starts(formatClassWhen(session.starts_at, course.timezone, readAt));
}

/** "7:00 to 8:30 PM": when it meets, in the course's zone. */
function meetingTime(workspace: SessionWorkspace): string {
  const { session, course } = workspace;
  const time = (at: string) => formatInCourseZone(at, course.timezone, { hour: "numeric", minute: "2-digit" });
  return `${time(session.starts_at)} to ${time(session.ends_at)}`;
}

/** Whether Teach offers Join: the server resolved a link this person may open. */
export function joinUrl(workspace: SessionWorkspace): string | null {
  const { destination, viewer, session } = workspace;
  if (session.status === "cancelled" || !destination.join_url) return null;
  const offered = destination.resolution !== undefined
    ? destination.resolution_action === "join_meeting" || destination.resolution === "join_ready"
    : viewer.allowed_actions.includes("join_meeting");
  return offered ? destination.join_url : null;
}

/**
 * S02 Teach (CASE-023): a Fact. Its answer is the phase; Join leaves for the
 * provider in a new tab and names it (CASE-027). Where it meets is said once:
 * by Join's verb when there is a link, otherwise by the sentence that names
 * who sets it (C4, R1). The teaching team edits where it meets in its body.
 */
export function useTeachBox({
  workspace,
  read,
  teaching,
  rank,
  body,
  changes = false,
}: {
  workspace: SessionWorkspace;
  read: Read<SessionWorkspace>;
  teaching: boolean;
  rank: number | null;
  /** The destination editor and the staff's meeting details, drawn open. */
  body?: ReactNode;
  /** Whether the body offers a change (the editor or a repair), or only reads where it meets. */
  changes?: boolean;
}): FactBox<SessionWorkspace> {
  const url = joinUrl(workspace);
  const readAt = read.status === "ready" ? read.readAt : read.status === "stale" ? read.readAt : new Date().toISOString();
  const candidate: Candidate | undefined = url ? { eligible: true, blocked: null, rank } : undefined;
  return {
    kind: "fact",
    id: "teach",
    job: copy.job,
    label: copy.label,
    owner: { system: "imam" },
    read,
    prose: (value) => teachProse(value, readAt),
    detail: (value) => meetingTime(value),
    sub: url ? undefined : (value) => destinationStatement(value, teaching),
    act: url ? { verb: copy.join(workspace.destination.meeting_provider), does: "leave", providerUrl: url } : undefined,
    candidate,
    body: body ? () => body : undefined,
    // Join is the face's one act (F1): with it, Teach stays open, its head keeps Join (CASE-047) and its body is drawn.
    exits: body && !url ? [{ label: changes ? copy.change : copy.whereLabel, reopens: true }] : undefined,
    fold: url ? "never" : "choice",
  };
}
